import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Download, LogOut, Trash2, Loader2, MailCheck, MailX } from "lucide-react";
import { toast } from "sonner";
import { http, errorText } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Logo } from "../components/site/Logo";
import { useTitle } from "../lib/useTitle";

const STATUSES = ["new", "contacted", "closed"];
const COLORS = { new: "bg-amber-100 text-amber-900", contacted: "bg-sky-100 text-sky-900", closed: "bg-emerald-100 text-emerald-900" };

const toCsv = (rows) => {
  const cols = ["created_at", "enquiry_type", "name", "company", "email", "phone", "country", "product", "species", "message", "status"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
};

export default function Admin() {
  useTitle("Enquiries | Gait Naurish Ltd.");
  const { user, logout } = useAuth();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!user) return undefined;
    http.get("/admin/enquiries")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch((e) => { toast.error(errorText(e.response?.data?.detail)); setItems([]); });
    return undefined;
  }, [user]);

  const shown = useMemo(() => (items || []).filter((i) => filter === "all" || i.status === filter), [items, filter]);

  if (user === null) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin" /></div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const setStatus = async (id, status) => {
    try {
      const { data } = await http.patch(`/admin/enquiries/${id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === id ? data : x)));
    } catch (e) { toast.error(errorText(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await http.delete(`/admin/enquiries/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
    } catch (e) { toast.error(errorText(e.response?.data?.detail)); }
  };

  const exportCsv = () => {
    const url = URL.createObjectURL(new Blob([toCsv(shown)], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "enquiries.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main data-testid="admin-page" className="min-h-screen bg-[var(--bone)]">
      <header className="border-b border-[var(--line)] bg-[var(--paper)]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-4 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <Link to="/admin/news" data-testid="admin-news-link" className="inline-flex items-center gap-2 rounded-full border border-[var(--line)] px-4 py-2 text-sm font-semibold">News articles</Link>
            <button data-testid="admin-logout-button" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-[var(--line)] px-4 py-2 text-sm"><LogOut size={14} /> Sign out</button>
          </div>
        </div>
      </header>
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl text-[var(--forest)]">Enquiries</h1>
            <p data-testid="admin-count" className="text-[var(--muted)] mt-1">{(items || []).length} total · {(items || []).filter((i) => i.status === "new").length} new</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {["all", ...STATUSES].map((s) => (
              <button key={s} data-testid={`admin-filter-${s}`} onClick={() => setFilter(s)} className={`rounded-full px-4 py-2 text-sm capitalize border ${filter === s ? "bg-[var(--forest)] text-[var(--bone)] border-[var(--forest)]" : "border-[var(--line)]"}`}>{s}</button>
            ))}
            <button data-testid="admin-export-button" onClick={exportCsv} className="rounded-full px-4 py-2 text-sm border border-[var(--line)] inline-flex items-center gap-2"><Download size={14} /> CSV</button>
          </div>
        </div>
        <div className="mt-8 space-y-3">
          {items === null && <Loader2 className="animate-spin" />}
          {items && shown.length === 0 && <p data-testid="admin-empty" className="text-[var(--muted)] py-10">No enquiries yet.</p>}
          {shown.map((e) => (
            <article key={e.id} data-testid={`admin-enquiry-${e.id}`} className="rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-5 md:p-6 grid md:grid-cols-12 gap-4">
              <div className="md:col-span-4">
                <p className="font-display text-xl text-[var(--forest)]">{e.name}</p>
                <p className="text-sm">{e.company}</p>
                <a href={`mailto:${e.email}`} className="text-sm text-[var(--moss)] link-underline">{e.email}</a>
                {e.phone && <p className="text-sm text-[var(--muted)]">{e.phone}</p>}
                <p className="font-mono text-[11px] text-[var(--muted)] mt-2">{new Date(e.created_at).toLocaleString()} · {e.country}</p>
              </div>
              <div className="md:col-span-5">
                <p className="font-mono text-xs text-[var(--amber)]">{e.enquiry_type} · {e.product}{e.species ? ` · ${e.species}` : ""}</p>
                <p className="text-sm mt-2 whitespace-pre-line text-[var(--ink)]">{e.message || "—"}</p>
              </div>
              <div className="md:col-span-3 flex md:flex-col md:items-end gap-2 flex-wrap">
                <span className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${COLORS[e.status]}`}>{e.status}</span>
                <span className="inline-flex items-center gap-1 text-xs text-[var(--muted)]">{e.email_sent ? <MailCheck size={14} /> : <MailX size={14} />} {e.email_sent ? "Email alert sent" : "Email alert not sent"}</span>
                <div className="flex gap-1">
                  {STATUSES.filter((s) => s !== e.status).map((s) => (
                    <button key={s} data-testid={`admin-set-${s}-${e.id}`} onClick={() => setStatus(e.id, s)} className="rounded-full border border-[var(--line)] px-3 py-1 text-xs capitalize hover:border-[var(--forest)]">Mark {s}</button>
                  ))}
                  <button data-testid={`admin-delete-${e.id}`} onClick={() => remove(e.id)} aria-label="Delete" className="rounded-full border border-[var(--line)] p-1.5 hover:border-red-700 hover:text-red-700"><Trash2 size={14} /></button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}
